import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const extractPostcode = (text) => {
  const match = text.match(
    /\b(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i
  );
  return match ? match[0].replace(/\s/g, '').toUpperCase() : null;
};

const lookupPostcode = async (text) => {
  const postcode = extractPostcode(text);

  if (!postcode) {
    throw new Error(
      'Please enter a full UK postcode in each location field.'
    );
  }

  const response = await fetch(
    `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
  );

  if (!response.ok) {
    throw new Error(`Could not find postcode ${postcode}.`);
  }

  const data = await response.json();

  if (
    !data.result ||
    typeof data.result.longitude !== 'number' ||
    typeof data.result.latitude !== 'number'
  ) {
    throw new Error(`No coordinates found for ${postcode}.`);
  }

  return {
    postcode,
    coordinates: [data.result.longitude, data.result.latitude]
  };
};

export default function RouteMap({ pickup, dropoff }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const requestRef = useRef(0);
  const showRouteRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [directionsUrl, setDirectionsUrl] = useState('');

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-2.5, 54.2],
      zoom: 5
    });

    map.addControl(
      new maplibregl.NavigationControl(),
      'top-right'
    );

    mapRef.current = map;

    return () => {
      requestRef.current += 1;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Clear an old route if the customer changes a location.
  useEffect(() => {
    requestRef.current += 1;
    setLoading(false);
    setSummary(null);
    setError('');
    setDirectionsUrl('');

    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (map.getLayer('usl-route-line')) {
      map.removeLayer('usl-route-line');
    }
    if (map.getSource('usl-route')) {
      map.removeSource('usl-route');
    }
  }, [pickup, dropoff]);

  const showRoute = async () => {
    if (!pickup.trim() || !dropoff.trim()) {
      setError('Enter both pickup and delivery postcodes.');
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    setSummary(null);

    try {
      const start = await lookupPostcode(pickup);
      const end = await lookupPostcode(dropoff);

      if (requestId !== requestRef.current) return;

      const a = start.coordinates;
      const b = end.coordinates;

      const externalUrl =
        'https://www.openstreetmap.org/directions' +
        '?engine=fossgis_osrm_car' +
        '&route=' +
        encodeURIComponent(`${a[1]},${a[0]};${b[1]},${b[0]}`);

      setDirectionsUrl(externalUrl);

      const url =
        'https://routing.openstreetmap.de/routed-car/route/v1/driving/' +
        `${a[0]},${a[1]};${b[0]},${b[1]}` +
        '?overview=full&geometries=geojson&steps=false';

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('The routing service is temporarily unavailable.');
      }

      const data = await response.json();
      const route = data.routes?.[0];

      if (
        data.code !== 'Ok' ||
        !route ||
        !Array.isArray(route.geometry?.coordinates)
      ) {
        throw new Error('No driving route was found.');
      }

      if (requestId !== requestRef.current) return;

      const map = mapRef.current;
      if (!map) return;

      if (!map.isStyleLoaded()) {
        await new Promise(resolve => map.once('load', resolve));
      }

      if (requestId !== requestRef.current) return;

      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      if (map.getLayer('usl-route-line')) {
        map.removeLayer('usl-route-line');
      }
      if (map.getSource('usl-route')) {
        map.removeSource('usl-route');
      }

      map.addSource('usl-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: route.geometry
        }
      });

      map.addLayer({
        id: 'usl-route-line',
        type: 'line',
        source: 'usl-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#00A8E8',
          'line-width': 5,
          'line-opacity': 0.9
        }
      });

      const pickupMarker = new maplibregl.Marker({
        color: '#00A8E8'
      })
        .setLngLat(a)
        .setPopup(
          new maplibregl.Popup().setText(
            `Collection: ${start.postcode}`
          )
        )
        .addTo(map);

      const deliveryMarker = new maplibregl.Marker({
        color: '#10243D'
      })
        .setLngLat(b)
        .setPopup(
          new maplibregl.Popup().setText(
            `Delivery: ${end.postcode}`
          )
        )
        .addTo(map);

      markersRef.current = [pickupMarker, deliveryMarker];

      const bounds = new maplibregl.LngLatBounds();

      route.geometry.coordinates.forEach(point => {
        bounds.extend(point);
      });

      map.fitBounds(bounds, {
        padding: 45,
        maxZoom: 13,
        duration: 700
      });

      setSummary({
        miles: (route.distance / 1609.344).toFixed(1),
        minutes: Math.round(route.duration / 60)
      });

    } catch (err) {
      if (requestId === requestRef.current) {
        setError(err.message || 'Could not calculate the route.');
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  };

  /* AUTO ROUTE CALCULATION */
  // Always reference the latest version of showRoute.
  showRouteRef.current = showRoute;

  useEffect(() => {
    // Wait until both fields contain full UK postcodes.
    if (!extractPostcode(pickup) || !extractPostcode(dropoff)) {
      return;
    }

    // Debounce to avoid excessive requests while typing.
    const timer = setTimeout(() => {
      showRouteRef.current?.();
    }, 1000);

    return () => clearTimeout(timer);
  }, [pickup, dropoff]);

  return (
    <div className="usl-route-widget">
      <div
        ref={containerRef}
        className="usl-route-map"
        aria-label="Interactive collection and delivery map"
      />

      {summary && (
        <div className="usl-route-summary" role="status">
          <div>
            <strong>{summary.miles} miles</strong>
            <span>Driving distance</span>
          </div>
          <div>
            <strong>
              {Math.floor(summary.minutes / 60) > 0
                ? `${Math.floor(summary.minutes / 60)} hr `
                : ''}
              {summary.minutes % 60} min
            </strong>
            <span>Estimated driving time</span>
          </div>
        </div>
      )}

      {error && (
        <p className="usl-request-error" role="alert">
          {error}
        </p>
      )}

      {directionsUrl && (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="usl-request-route-link"
        >
          Open directions in OpenStreetMap
        </a>
      )}
    </div>
  );
}
